import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import FilterBar from '../../components/settings/FilterBar';
import IconAdd from '../../components/icons/IconAdd';
import IconEdit from '../../components/icons/IconEdit';
import IconDelete from '../../components/icons/IconDelete';
import IconCopyUrl from '../../components/icons/IconCopyUrl';
import StudentsLeaderboard from './StudentsLeaderboard';
import StudentCreateModal from './StudentCreateModal';
import language from '../../variables/language';
import '../../css/students-list.css';

const url = '/api/students';

const Students = () => {
   const navigate = useNavigate();
   const [students, setStudents] = useState([]);
   const [stats, setStats] = useState(null);
   const [search, setSearch] = useState('');
   const [isActive, setIsActive] = useState('');
   const [refresh, setRefresh] = useState(0);
   const [createOpen, setCreateOpen] = useState(false);
   const [copiedId, setCopiedId] = useState(null);

   useEffect(() => {
      const params = new URLSearchParams();
      if(search.trim()) params.set('search', search.trim());
      if(isActive) params.set('isActive', isActive);
      const qs = params.toString();
      axios.get(url + (qs ? '?' + qs : ''), { withCredentials: true })
         .then(response => setStudents(Array.isArray(response.data) ? response.data : []))
         .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); });
   }, [search, isActive, refresh, navigate]);

   useEffect(() => {
      axios.get(url + '/stats', { withCredentials: true })
         .then(response => setStats(response.data))
         .catch(() => setStats(null));
   }, [refresh]);

   const refreshList = () => setRefresh(prev => prev + 1);
   const closeCreate = () => setCreateOpen(false);
   const onCreated = () => { closeCreate(); refreshList(); };

   const lang = language[language.lang];

   const testsItems = useMemo(() => {
      if(!stats || !Array.isArray(stats.byTestsCompleted)) return [];
      return stats.byTestsCompleted.map(s => ({
         studentId: s.studentId, name: s.name, surname: s.surname,
         value: s.count, displayValue: String(s.count),
      }));
   }, [stats]);

   const accuracyItems = useMemo(() => {
      if(!stats || !Array.isArray(stats.byAccuracy)) return [];
      return stats.byAccuracy.map(s => ({
         studentId: s.studentId, name: s.name, surname: s.surname,
         value: s.pct, displayValue: s.pct + '%',
      }));
   }, [stats]);

   const lessonsItems = useMemo(() => {
      if(!stats || !Array.isArray(stats.byLessonsLastMonth)) return [];
      return stats.byLessonsLastMonth.map(s => ({
         studentId: s.studentId, name: s.name, surname: s.surname,
         value: s.hours, displayValue: s.hours + ' ' + lang.studentsPage.hoursShort,
      }));
   }, [stats, lang]);

   const lessonsCalendarItems = useMemo(() => {
      if(!stats || !Array.isArray(stats.byLessonsCalendarMonth)) return [];
      return stats.byLessonsCalendarMonth.map(s => ({
         studentId: s.studentId, name: s.name, surname: s.surname,
         value: s.hours, displayValue: s.hours + ' ' + lang.studentsPage.hoursShort,
      }));
   }, [stats, lang]);

   const handleDelete = (e, student) => {
      e.stopPropagation();
      if(!window.confirm('Czy na pewno chcesz usunąć ten element?')) return;
      axios.delete(url + '/' + student.id, { withCredentials: true })
         .then(() => refreshList())
         .catch(err => console.log(err));
   }

   const handleCopyInvite = (e, student) => {
      e.stopPropagation();
      const link = 'https://t.me/Eng_for_ukr_bot?start=' + student.hashCode;
      navigator.clipboard.writeText(link).then(() => {
         setCopiedId(student.id);
         setTimeout(() => setCopiedId(prev => prev === student.id ? null : prev), 1500);
      }).catch(() => {});
   }

   return (
      <Layout>
         <PageHeader name={lang.students}>
            <div className='students-header-stats'>
               <span className='students-header-stat-pill'>
                  <b>{stats ? stats.totalCount : students.length}</b>
                  <small>{lang.studentsPage.totalStudents}</small>
               </span>
               <span className='students-header-stat-pill'>
                  <b>{stats ? stats.activeCount : '—'}</b>
                  <small>{lang.studentsPage.activeStudents}</small>
               </span>
            </div>
            <FilterBar
               search={{ value: search, onChange: setSearch, placeholder: lang.placeholderSearch }}
               filters={[{
                  key: 'isActive', type: 'select', label: lang.studentsPage.filterAllStatus,
                  options: [{id:1, name:'Yes'}, {id:2, name:'No'}],
                  value: isActive, onChange: setIsActive, numeric: true,
               }]}
               resultCount={students.length} />
            <div className='students-header-actions'>
               <div className='students-add-btn' onClick={() => setCreateOpen(true)} title={lang.studentsPage.addStudent}>
                  <IconAdd />
               </div>
            </div>
         </PageHeader>
         <main className='main-block'>
            <div className='main-content txt-c students-page'>
               <div className='students-layout'>

                  <div className='students-overview-col'>
                     <StudentsLeaderboard title={lang.studentsPage.testsLeaderTitle} items={testsItems} />
                     <StudentsLeaderboard title={lang.studentsPage.accuracyLeaderTitle} items={accuracyItems} />
                     <StudentsLeaderboard title={lang.studentsPage.lessonsLeaderTitle} items={lessonsItems} />
                     <StudentsLeaderboard title={lang.studentsPage.lessonsCalendarLeaderTitle} items={lessonsCalendarItems} />
                  </div>

                  <div className='students-list-col'>
                     <table className='table students-table'>
                        <thead>
                           <tr>
                              <th>{lang.name}</th>
                              <th>{lang.responsibleId}</th>
                              <th>{lang.isActive}</th>
                              <th></th>
                           </tr>
                        </thead>
                        <tbody>
                           {students.map(student => (
                              <tr key={student.id} className='students-row' onClick={() => navigate('/students/' + student.id)}>
                                 <td>
                                    <div className='students-name-cell'>
                                       <div className='students-mini-avatar'>{((student.name || '?').charAt(0) + (student.surname || '').charAt(0)).toUpperCase()}</div>
                                       <span className='students-name-text'>{student.name} {student.surname}</span>
                                    </div>
                                 </td>
                                 <td>{student.responsibleId ? student.responsibleId.firstName + ' ' + student.responsibleId.lastName : ''}</td>
                                 <td>
                                    <span className={'students-status-pill' + (student.isActive === 1 ? ' on' : '')}>
                                       <i className='students-status-dot'></i>
                                       {student.isActive === 1 ? lang.Yes : lang.No}
                                    </span>
                                 </td>
                                 <td className='students-actions-cell' onClick={e => e.stopPropagation()}>
                                    <div className='students-icon-btn' onClick={() => navigate('/students/' + student.id)}>
                                       <IconEdit style={{fill:'#7ec2e8', width:14, height:14, margin:0}} />
                                    </div>
                                    <div className='students-icon-btn students-copy-btn' onClick={(e) => handleCopyInvite(e, student)} title={lang.url}>
                                       <IconCopyUrl style={{fill:'#8fc9c3', width:14, height:14, margin:0}} />
                                       {copiedId === student.id ? <span className='students-copied-hint'>{lang.studentsPage.copied}</span> : ''}
                                    </div>
                                    <div className='students-icon-btn' onClick={(e) => handleDelete(e, student)}>
                                       <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
                                    </div>
                                 </td>
                              </tr>
                           ))}
                           {students.length === 0 ? (
                              <tr className='students-empty-row'><td colSpan={4}>{lang['Empty list']}</td></tr>
                           ) : ''}
                        </tbody>
                     </table>
                  </div>

               </div>
            </div>
         </main>
         <StudentCreateModal isOpen={createOpen} onClose={closeCreate} onCreated={onCreated} />
      </Layout>
   );
}

export default Students;
